import React, { FC, useCallback, useEffect, useState } from 'react';

import { Show } from '@ariksa/compliance-policies/api';
import { Box, Stack, HStack, useDisclosure } from '@chakra-ui/react';
import isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import { useDeepCompareEffect } from 'react-use';

import { PrimaryButton, Select } from 'components/DataEntry';
import { ExceptionIcon } from 'components/Icons';
import { FilterLabelWrapper } from 'components/index';
import { RulesForResource } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Compliance/RulesForResource';
import { compliancePolicyStatusOptions } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Compliance/utils';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { actions } from 'containers/ActiveCloudResource/slice';
import { selectActiveEnvironment } from 'containers/App/selectors';
import { ComplianceContextProvider } from 'containers/Compliance/Components/hooks/context';
import { useBlueprintsMapping } from 'containers/SharedState/hooks/useBlueprintsMapping';

export interface Props {}

export const Compliance: FC<Props> = () => {
  const [blueprintOptions, setBlueprintsOptions] = useState<
    Record<string, any>[]
  >([]);
  const [currentBlueprint, setCurrentBlueprint] = useState<Record<string, any>>(
    {},
  );
  const {
    resourceType,
    resourceUuid,
    resourceAccountId,
    resourceInsight,
    rules,
  } = useSelector(selectActiveResource);
  const { environmentId } = useSelector(selectActiveEnvironment);
  const resourceEnvironmentId =
    resourceInsight.data?.environment_id ?? environmentId;

  const [status, setStatus] = useState(compliancePolicyStatusOptions[0]);
  const excludePoliciesDisclosure = useDisclosure();
  const [selectedPolicies, setSelectedPolicies] = useState<string[]>([]);
  const {
    envBlueprintMapping,
    getBlueprintOptions,
    getOptions: getBlueprintOptionsForData,
  } = useBlueprintsMapping();
  const dispatch = useDispatch();

  //set blueprint options
  useEffect(() => {
    const attachedBlueprints = resourceEnvironmentId
      ? envBlueprintMapping[resourceEnvironmentId] ?? []
      : [];

    setBlueprintsOptions(
      resourceEnvironmentId
        ? getBlueprintOptionsForData(attachedBlueprints)
        : getBlueprintOptions(),
    );
  }, [
    envBlueprintMapping,
    getBlueprintOptions,
    getBlueprintOptionsForData,
    resourceEnvironmentId,
  ]);

  useEffect(() => {
    setCurrentBlueprint(
      blueprintOptions.find(
        blueprint => blueprint?.label === 'Ariksa Best Practices',
      ) ?? blueprintOptions[0],
    );
  }, [blueprintOptions]);

  const getRules = useCallback(() => {
    dispatch(
      actions.loadRules({
        q: {
          blueprintId: currentBlueprint?.data?.id,
          resourceId: resourceUuid,
          show: status?.value || Show.All,
          page: rules.page.info.page_number,
          size: rules.page.info.page_size,
        },
      }),
    );
  }, [currentBlueprint, resourceUuid, status, rules.page.info, dispatch]);

  useDeepCompareEffect(() => {
    if (!isEmpty(currentBlueprint)) getRules();
  }, [currentBlueprint, getRules]);

  return (
    <Stack w="full" h="full" spacing={5}>
      <HStack justify="space-between">
        <HStack>
          <FilterLabelWrapper>FILTER BY</FilterLabelWrapper>
          <Box>
            <Select
              options={blueprintOptions}
              value={currentBlueprint}
              onChange={setCurrentBlueprint}
              menuPortalTarget={document.body}
              showIconInValueContainer
              isAFilter
              leftMessage="Standard"
              showClearIndicator={false}
            />
          </Box>
          <Box>
            <Select
              options={compliancePolicyStatusOptions}
              value={status}
              onChange={setStatus}
              menuPortalTarget={document.body}
              showIconInValueContainer
              isAFilter
              leftMessage="Status"
              showUndefinedValueLabel={false}
            />
          </Box>
        </HStack>
        <PrimaryButton
          leftIcon={<ExceptionIcon />}
          isDisabled={!selectedPolicies?.length}
          onClick={() => {
            excludePoliciesDisclosure.onOpen();
          }}
        >
          Exclude Policies
        </PrimaryButton>
      </HStack>
      <Box w="full" h="xl">
        <ComplianceContextProvider>
          <RulesForResource
            showRuleId={false}
            selectedResource={{
              ...resourceInsight.data,
              resource_id: resourceUuid,
              type: resourceType,
              account_id: resourceAccountId,
            }}
            selectedBlueprint={currentBlueprint?.data}
            tableStyles={{ header: { position: 'relative', zIndex: 800 } }}
            fromCompliance={false}
            excludePoliciesDisclosure={excludePoliciesDisclosure}
            selectedPolicies={selectedPolicies}
            setSelectedPolicies={setSelectedPolicies}
            getRules={getRules}
          />
        </ComplianceContextProvider>
      </Box>
    </Stack>
  );
};
